// suppliers.js — column config + the server adapter wired to getSuppliers.sz.
import { createElement as h } from "react";
import { makeServerAdapter } from "../components/datatable/serverAdapter";

export const suppliersTableConfig = {
   tableId: "suppliers",
   rowIdField: "supplierId",
   defaultSort: { field: "name", dir: "asc" },
   showSearch: false,
   gearInHeader: true,           // cog sits inline in the header row (Documents/Statements-style)
   quickFilter: {
      param: "_payee_state",
      label: "Payee state",
      defaultValue: "all",
      countsFrom: "state_counts",
      options: [
         { value: "all", label: "All" },
         { value: "payee", label: "Payees" },
         { value: "unreviewed", label: "Unreviewed" },
         { value: "retired", label: "Retired" },
      ],
   },
   columns: [
      { field: "supplierId", label: "ID", type: "string", filterable: false, sortable: false, defaultVisible: false, excludeFromDropdown: true },
      {
         // Row click opens the detail panel; the anchor is the visual affordance
         // (Documents-style blue link), so it only needs to not hijack the click.
         field: "name", label: "Supplier", type: "string", filterable: true, searchable: true, sortable: true, defaultVisible: true,
         renderCell: (v) => h("a", { href: "#", className: "supplier-name-link", onClick: (e) => e.preventDefault() }, v),
      },
      {
         // Three states from the server's userRatified flag (a user decided this is the
         // payee: merged-in names, relink target, or onboarding-confirmed alias):
         //   Payee (ratified) · — (imported, unreviewed) · Retired (merged away).
         field: "payee", label: "Payee", type: "string", filterable: false, sortable: true, defaultVisible: true,
         renderCell: (_v, row) => {
            if (Number(row?.aliasCount) === 0) {
               return h("span", { className: "payee-badge retired", title: "Merged into another supplier — its names and history moved there" }, "Retired");
            }
            if (row?.userRatified === true) {
               return h("span", { className: "payee-badge live", title: "A user confirmed this supplier as a payee (merge, payee change, or onboarding)" }, "Payee");
            }
            return h("span", { className: "payee-none", title: "Imported from the ERP — no one has reviewed this supplier yet" }, "—");
         },
      },
      { field: "normalizedName", label: "Normalized", type: "string", filterable: true, searchable: true, sortable: true, defaultVisible: false },
      { field: "province", label: "Province", type: "string", filterable: true, sortable: true, defaultVisible: true },
      { field: "city", label: "City", type: "string", filterable: true, searchable: true, sortable: true, defaultVisible: true },
      { field: "street", label: "Street", type: "string", filterable: true, sortable: false, defaultVisible: false },
      { field: "postalCode", label: "Postal", type: "string", filterable: true, sortable: false, defaultVisible: false },
      { field: "country", label: "Country", type: "string", filterable: true, sortable: true, defaultVisible: false },
      // aliasCount stays in the payload (Retired badge derives from it) but is not a column.
      // "Other names" counts aliases whose raw text reads differently from the supplier's
      // display name — the supplier's own name is not an "other" name.
      {
         field: "otherNames", label: "Other names", type: "number", filterable: false, sortable: true, defaultVisible: true,
         renderCell: (v) => (Number(v) > 0 ? Number(v).toLocaleString() : h("span", { className: "payee-none" }, "—")),
      },
      // Trailing host for the in-header settings gear (empty label, blank body cells) so the
      // gear gets its own slot at the right edge instead of sharing the "Other names" column —
      // keeps the "Other names" counts aligned under their own header. Mirrors Statements'
      // __actions column. See gearInHeader handling in components/datatable/DataTable.jsx.
      { field: "__gear", label: "", type: "string", filterable: false, sortable: false, defaultVisible: true, excludeFromDropdown: true, align: "right" },
   ],
};

// v1 filter transport = named per-column params (getDocs.sz precedent). The generic
// {field,op,value} array is deferred (see spec §5.1).
function suppliersFormData(queryState) {
   const { pageIndex, pageSize, sort, filters, quickFilter } = queryState;
   const fd = new FormData();
   fd.append("_page_index", pageIndex ?? 0);
   fd.append("_page_size", pageSize ?? 10);
   fd.append("_payee_state", quickFilter || "all");
   if (sort?.field) {
      fd.append("_sort_field", sort.field);
      fd.append("_sort_dir", sort.dir || "asc");
   }
   const textFields = ["name", "normalizedName", "province", "city", "street", "postalCode", "country"];
   textFields.forEach((f) => {
      const v = filters?.[f];
      if (v != null && v !== "") fd.append(`_${f}`, v);
   });
   return fd;
}

export function makeSuppliersAdapter() {
   return makeServerAdapter({
      endpoint: "/getSuppliers.sz",
      buildFormData: (queryState) => suppliersFormData(queryState),
   });
}

// kept for any other importer
export const suppliersAdapter = makeSuppliersAdapter();
